<template>
  <div class="brand-panel">
    <div v-if="loading" class="state">加载中...</div>
    <div v-else-if="error" class="state error">{{ error }}</div>
    <div v-else-if="!hasData" class="state empty-hint">
      {{ emptyHint }}
    </div>
    <template v-else>
      <p v-if="data.updatedAtLabel" class="updated-at">{{ data.updatedAtLabel }}</p>
      <div class="table-scroll">
        <table class="platform-table">
          <thead>
            <tr>
              <th class="col-platform" colspan="2">平台数据</th>
              <th v-for="account in accounts" :key="account" class="col-account">{{ account }}</th>
            </tr>
          </thead>
          <tbody>
            <template v-for="(section, sectionIndex) in sections">
              <tr
                v-for="(row, rowIndex) in section.rows"
                :key="section.platform + row.code"
                :class="[
                  'theme-' + section.theme,
                  { 'section-start': rowIndex === 0 && sectionIndex > 0 }
                ]"
              >
                <td
                  v-if="rowIndex === 0"
                  class="platform-label"
                  :rowspan="section.rowCount"
                >
                  <span>{{ section.label }}</span>
                </td>
                <td class="metric-label">{{ row.label }}</td>
                <td
                  v-for="account in accounts"
                  :key="account + row.code"
                  class="metric-value"
                  :class="{ 'is-zero': isZeroValue(row.values, account) }"
                >
                  {{ displayValue(row.values, account) }}
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>
    </template>
  </div>
</template>

<script>
import coreapi from '@/utils/coreapi';

const EMPTY_HINT = '暂无品牌平台数据。可在品牌端完成抖音/快手 OAuth 授权后调用统计同步，或在 okradmin「品牌数据导入」上传 Excel';

export default {
  name: 'BrandPerformancePanel',
  props: {
    date: { type: String, default: '' },
    unit: { type: String, default: '' }
  },
  data() {
    return {
      loading: false,
      error: '',
      data: {
        hasData: false,
        updatedAtLabel: '',
        accounts: [],
        sections: []
      },
      emptyHint: EMPTY_HINT
    };
  },
  computed: {
    accounts() {
      return this.data.accounts || [];
    },
    sections() {
      return this.data.sections || [];
    },
    hasData() {
      if (typeof this.data.hasData === 'boolean') {
        return this.data.hasData;
      }
      return this.accounts.length > 0 || this.sections.length > 0;
    }
  },
  watch: {
    date() {
      this.load();
    },
    unit() {
      this.load();
    }
  },
  mounted() {
    this.load();
  },
  methods: {
    async load() {
      this.loading = true;
      this.error = '';
      try {
        const asOf = this.date || this.today();
        this.data = await coreapi.bossDepartmentBrand(asOf, this.unit);
      } catch (e) {
        this.error = e.message || '加载失败';
        this.data = { hasData: false, updatedAtLabel: '', accounts: [], sections: [] };
      } finally {
        this.loading = false;
      }
    },
    today() {
      const d = new Date();
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    },
    displayValue(values, account) {
      const raw = values && values[account];
      if (raw === null || raw === undefined || raw === '') {
        return '0';
      }
      return raw;
    },
    isZeroValue(values, account) {
      const text = String(this.displayValue(values, account));
      return text === '0' || text === '0%';
    }
  }
};
</script>

<style lang="less" scoped>
.brand-panel {
  padding: 0 0 24px;
  background: #fff;
}

.updated-at {
  margin: 0;
  padding: 10px 16px 8px;
  font-size: 12px;
  color: #8a919c;
  text-align: right;
}

.table-scroll {
  overflow-x: auto;
  padding: 0 12px 16px;
  -webkit-overflow-scrolling: touch;
}

.platform-table {
  width: 100%;
  min-width: 680px;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 13px;
  table-layout: fixed;
  border: 1px solid #e6e9ef;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.04);

  th,
  td {
    border-right: 1px solid #eef1f5;
    border-bottom: 1px solid #eef1f5;
    padding: 10px 8px;
    text-align: center;
    vertical-align: middle;
    word-break: break-word;
  }

  th:last-child,
  td:last-child {
    border-right: none;
  }

  tbody tr:last-child td {
    border-bottom: none;
  }

  thead th {
    background: linear-gradient(180deg, #fcfcfd 0%, #f4f6f9 100%);
    color: #2c3340;
    font-weight: 600;
    font-size: 13px;
    border-bottom: 2px solid #e3e7ee;
  }

  .col-platform {
    width: 92px;
  }

  .col-account {
    min-width: 100px;
    color: #1f2937;
  }

  .platform-label {
    width: 40px;
    padding: 6px 4px;
    color: #fff;
    font-weight: 600;
    font-size: 12px;
    writing-mode: vertical-rl;
    text-orientation: upright;
    letter-spacing: 3px;
    border-right: none;

    span {
      display: inline-block;
    }
  }

  .metric-label {
    text-align: left;
    padding: 10px 12px;
    color: #374151;
    background: #fff;
    min-width: 140px;
    font-size: 12px;
    line-height: 1.45;
  }

  .metric-value {
    color: #111827;
    background: #fff;
    min-height: 38px;
    text-align: right;
    padding-right: 14px;
    font-variant-numeric: tabular-nums;
    font-weight: 500;
    letter-spacing: 0.02em;

    &.is-zero {
      color: #a8b0bd;
      font-weight: 400;
    }
  }

  tbody tr:nth-child(even) .metric-label,
  tbody tr:nth-child(even) .metric-value {
    background: #fafbfc;
  }

  tr.section-start .metric-label,
  tr.section-start .metric-value {
    border-top: 2px solid #e3e7ee;
  }

  tr.theme-wechat .platform-label {
    background: linear-gradient(180deg, #a03345 0%, #8f2d3b 100%);
  }

  tr.theme-douyin .platform-label {
    background: linear-gradient(180deg, #2a508f 0%, #1f3f7a 100%);
  }

  tr.theme-kuaishou .platform-label {
    background: linear-gradient(180deg, #27855a 0%, #1f6b45 100%);
  }
}

.state {
  padding: 60px 20px;
  text-align: center;
  color: #9aa1b2;

  &.error {
    color: #d9272e;
  }

  &.empty-hint {
    padding: 48px 24px;
    line-height: 1.6;
    font-size: 14px;
  }
}
</style>



// WEBPACK FOOTER //
// src/views/boss/BrandPerformancePanel.vue